import {
  DIAMOND_PRODUCT_HANDLE,
  DIAMOND_WEIGHT_OPTION_NAME,
  HIDDEN_PRODUCT_TAG,
  METAL_OPTION_NAME,
  SUPPORTED_SHAPES,
  SUPPORTED_WEIGHTS,
} from '@shared/lib/constants';
import { getProduct } from '@shared/lib/shopify';
import {
  Connection,
  Image,
  Product,
  ProductVariant,
  ProductVariantOption,
  ShopifyProduct,
  ShortProductVariant,
  ShortShopifyProduct,
  Video,
} from '@shared/lib/shopify/types';
import {
  DiamondProduct,
  DiamondVariant,
  ExternalVideo,
  JewelryProduct,
  JewelryVariant,
  RingImage,
  RingVideo,
  ShortJewelryProduct,
  ShortJewelryVariant,
  ShortProduct,
} from '@shared/lib/shopify/types/shopify-native';
import { assertPresent, parseIntOrUndefined } from '../utils';

export function decodeShopifyImageAlt(alt: string): {
  color?: string;
  centerShape?: string;
  viewPoint?: string;
  type?: string;
} {
  const tags = alt?.split(/[, ]/) ?? [];
  let shape = tags[1];
  if (shape === 'Square') {
    shape = 'Princess';
  }
  return {
    color: tags[0],
    centerShape: shape,
    viewPoint: tags[2],
    type: tags[3],
  };
}

function decodeShopifyVideoAlt(alt: string): {
  color?: string;
  centerShape?: string;
} {
  const tags = alt?.split(/[, ]/) ?? [];
  let shape = tags[1];
  if (shape === 'Square') {
    shape = 'Princess';
  }
  return {
    color: tags[0],
    centerShape: shape,
  };
}

export function groupRingMedia(
  images: Image[],
  video: Video[]
): {
  images: RingImage[];
  videos: RingVideo[];
  availableImageColors: string[];
  availableVideoColors: string[];
  availableImageCenterShapes: string[];
  availableVideoCenterShapes: string[];
} {
  const ringImages = images.map((image) => ({
    ...image,
    ...decodeShopifyImageAlt(image.altText),
  }));
  const ringVideos = video.map((video) => ({
    ...video,
    ...decodeShopifyVideoAlt(video.altText),
  }));
  const availableImageColors = Array.from(
    new Set(ringImages.map((image) => image.color).filter(Boolean) as string[])
  );
  const availableVideoColors = Array.from(
    new Set(ringVideos.map((video) => video.color).filter(Boolean) as string[])
  );
  const availableImageCenterShapes = Array.from(
    new Set(ringImages.map((image) => image.centerShape).filter(Boolean) as string[])
  );
  const availableVideoCenterShapes = Array.from(
    new Set(ringVideos.map((video) => video.centerShape).filter(Boolean) as string[])
  );
  return {
    images: ringImages,
    videos: ringVideos,
    availableImageColors,
    availableVideoColors,
    availableImageCenterShapes,
    availableVideoCenterShapes,
  };
}

export function filterRingMedia(
  ringMedia: RingImage[] | RingVideo[] | ExternalVideo[],
  color: string | null = 'white',
  centerShapes: string[] = ['DefaultCut']
): {
  result: RingImage[] | RingVideo[] | ExternalVideo[];
  exactMatch: boolean;
} {
  let exactMatch = true;
  let colorMatchedMedia = ringMedia.filter((image) => image.color === color);
  if (colorMatchedMedia.length === 0) {
    colorMatchedMedia = ringMedia;
    if (color !== null) {
      exactMatch = false;
    }
  }
  let centerShapeMatchedMedia = colorMatchedMedia.filter((media) =>
    centerShapes.some((shape) => shape === media.centerShape)
  );
  if (centerShapeMatchedMedia.length === 0) {
    centerShapeMatchedMedia = colorMatchedMedia.filter(
      (media) => media.centerShape === 'DefaultCut'
    );
    exactMatch = false;
  }
  if (centerShapeMatchedMedia.length === 0) {
    centerShapeMatchedMedia = colorMatchedMedia.filter((media) => media.centerShape === 'Round');
    exactMatch = false;
  }
  if (centerShapeMatchedMedia.length === 0) {
    centerShapeMatchedMedia = colorMatchedMedia;
    exactMatch = false;
  }
  return {
    result: centerShapeMatchedMedia,
    exactMatch,
  };
}

export function refineJewelryVariants(
  variants: ProductVariant[],
  metal: string | undefined | null,
  centerDiamondWeight: string | undefined | null
): ProductVariant[] {
  return variants.filter((variant) => {
    return (
      (!metal ||
        variant.selectedOptions.some(
          (option) => option.name === METAL_OPTION_NAME && option.value === metal
        )) &&
      (!centerDiamondWeight ||
        variant.selectedOptions.some(
          (option) =>
            option.name === DIAMOND_WEIGHT_OPTION_NAME &&
            option.value.replace(' tw', '') === centerDiamondWeight
        ))
    );
  });
}

export function getsupportedShapes(tags: string[]): string[] {
  if (tags.find((tag) => tag === 'Take Peg Head')) {
    return SUPPORTED_SHAPES;
  }
  return tags.filter((tag) => SUPPORTED_SHAPES.includes(tag));
}

export function getCaratTitle(options: ProductVariantOption[]): string {
  return options.find((option) => option.name === 'Size')?.value!;
}

export function getCutTitle(options: ProductVariantOption[]): string {
  return options.find((option) => option.name === 'Cut')?.value!;
}

export async function getCutVariantLists(): Promise<Record<string, DiamondVariant[]>> {
  const diamondProduct = assertPresent(await getProduct(DIAMOND_PRODUCT_HANDLE));
  const reshapedDiamondProduct = reshapeDiamondProduct(diamondProduct);
  return reshapedDiamondProduct.variants.reduce(
    (acc, variant) => {
      const cutOption = variant.shape;
      if (cutOption) {
        if (!acc[cutOption]) {
          acc[cutOption] = [];
        }
        acc[cutOption]!.push(variant);
      }
      return acc;
    },
    {} as Record<string, DiamondVariant[]>
  );
}

export async function getCutVariants(): Promise<Record<string, DiamondVariant>> {
  const cutVariantLists = await getCutVariantLists();
  return Object.fromEntries(
    Object.entries(cutVariantLists).map(([cut, variants]) => [cut.toLowerCase(), variants[0]!])
  );
}

const relativeImageSizes = {
  '1/4 ct. +': 7.6,
  '1/2 ct. +': 9.6,
  '3/4 ct. +': 10.8,
  '1 ct. +': 12.0,
  '1 1/2 ct. +': 13.6,
  '2 ct. +': 15.2,
  '3 ct. +': 17.2,
};
const cutImageRelativeSizes = {
  Round: 1,
  Emerald: 0.9,
  Cushion: 1,
  Princess: 1,
  Pear: 1,
  Oval: 0.9,
  Marquise: 0.9,
  Heart: 1,
  Radiant: 0.9,
  Asscher: 1,
};

// Convert carat string to number (e.g., "1/2 ct. +" -> 0.5, "1 1/2 ct. +" -> 1.5)
export const getCaratValue = (caratString: string): number => {
  // Remove " ct" or any trailing text after the carat value
  const cleanedString = caratString.replace(/\s*(?:ct|CT).*$/, '');

  // Check if the string contains a fraction
  if (cleanedString.includes('/')) {
    // Handle mixed numbers like "1 1/2"
    if (cleanedString.includes(' ')) {
      const [whole, fraction] = cleanedString.split(' ');
      const [numerator, denominator] = fraction?.split('/').map(Number) ?? [0, 1];
      return parseInt(whole!) + numerator! / denominator!;
    } else {
      // Handle simple fractions like "1/2"
      const [numerator, denominator] = cleanedString.split('/').map(Number);
      return numerator! / denominator!;
    }
  } else {
    // Handle whole numbers like "1" or "2"
    return parseFloat(cleanedString);
  }
};

export const calculateDiamondImageSize = (
  carat: string | number,
  shape: string,
  ratio: number = 1
) => {
  const caratValue = typeof carat === 'string' ? getCaratValue(carat) : carat;
  return (
    caratValue ** (1 / 3) *
    12 *
    cutImageRelativeSizes[shape as keyof typeof cutImageRelativeSizes] *
    ratio
  );
};

export const removeEdgesAndNodes = (array: Connection<any>) => {
  return array.edges.map((edge) => edge?.node);
};

export const reshapeProduct = (
  product: ShopifyProduct,
  filterHiddenProducts: boolean = true
): Product | undefined => {
  if (!product || (filterHiddenProducts && product.tags.includes(HIDDEN_PRODUCT_TAG))) {
    return undefined;
  }

  const { media, variants, ...rest } = product;
  const flattenedMedia = removeEdgesAndNodes(media).filter(
    (m) => m.mediaContentType === 'IMAGE' || m.mediaContentType === 'VIDEO'
  );

  return {
    ...rest,
    images: flattenedMedia
      .filter((m) => m.mediaContentType === 'IMAGE')
      .map((m) => ({
        id: m.id,
        ...m.image,
        altText: m.alt,
      })) as Image[],
    videos: flattenedMedia
      .filter((m) => m.mediaContentType === 'VIDEO')
      .map((m) => ({
        id: m.id,
        altText: m.alt,
        ...m.sources[0],
      })) as Video[],
    variants: removeEdgesAndNodes(variants),
  };
};

export const reshapeDiamondProduct = (product: Product): DiamondProduct => {
  const { variants, ...rest } = product;
  return {
    ...rest,
    variants: variants.map(reshapeDiamondVariant),
  };
};

export const reshapeDiamondVariant = (variant: ProductVariant): DiamondVariant => {
  return variant.selectedOptions.reduce(
    (acc, option) => {
      if (!option) {
        return acc;
      }
      if (option.name === 'Cut') {
        acc.shape = option.value;
      }
      if (option.name === 'Size') {
        acc.carat = getCaratValue(option.value);
        acc.caratDisplay = option.value;
      }
      return acc;
    },
    {
      ...variant,
      shape: '',
      carat: 0,
      caratDisplay: '',
    } as DiamondVariant
  );
};

export const reshapeProducts = (products: ShopifyProduct[]): Product[] => {
  const reshapedProducts = [];

  for (const product of products) {
    if (product) {
      const reshapedProduct = reshapeProduct(product);

      if (reshapedProduct) {
        reshapedProducts.push(reshapedProduct);
      }
    }
  }

  return reshapedProducts;
};

export const reshapeJewelryProduct = (product: Product): JewelryProduct => {
  return {
    ...product,
    variants: product.variants.map(reshapeJewelryVariant),
    metalOptions: product.options.find((option) => option.name === METAL_OPTION_NAME)?.values ?? [],
    diamondWeightOptions:
      product.options.find((option) => option.name === DIAMOND_WEIGHT_OPTION_NAME)?.values ??
      SUPPORTED_WEIGHTS,
  };
};

export const reshapeShortProduct = (product: ShortShopifyProduct): ShortProduct => {
  return {
    ...product,
    variants: removeEdgesAndNodes(product.variants),
    images: removeEdgesAndNodes(product.images),
  };
};

export const reshapeShortJewelryProduct = (product: ShortShopifyProduct): ShortJewelryProduct => {
  return {
    ...product,
    variants: removeEdgesAndNodes(product.variants).map(reshapeShortJewelryVariant),
    images: removeEdgesAndNodes(product.images).map((image) => ({
      ...image,
      ...decodeShopifyImageAlt(image.altText),
    })) as RingImage[],
  };
};

export const reshapeShortJewelryVariant = (variant: ShortProductVariant): ShortJewelryVariant => {
  const { metafields, selectedOptions, ...rest } = variant;
  const fractionCenter =
    selectedOptions.find((option) => option.name === DIAMOND_WEIGHT_OPTION_NAME)?.value ?? null;
  const centerCarat = fractionCenter ? getCaratValue(fractionCenter) : null;
  return metafields.reduce(
    (acc, metafield) => {
      if (!metafield) {
        return acc;
      }
      if (metafield.key === 'side_diamond_number') {
        acc.sideDiamondNumber = parseIntOrUndefined(metafield.value);
      }
      return acc;
    },
    {
      ...rest,
      metal: selectedOptions.find((option) => option.name === METAL_OPTION_NAME)?.value!,
      fractionCenter,
      centerCarat,
    } as ShortJewelryVariant
  );
};

export const reshapeJewelryVariant = (variant: ProductVariant): JewelryVariant => {
  const { metafields, ...rest } = variant;
  const fractionCenter =
    variant.selectedOptions.find((option) => option.name === DIAMOND_WEIGHT_OPTION_NAME)?.value ??
    null;
  const centerCarat = fractionCenter ? getCaratValue(fractionCenter) : null;
  return metafields.reduce(
    (acc, metafield) => {
      if (!metafield) {
        return acc;
      }
      if (metafield.key === 'sku') {
        acc.sku = metafield.value;
      }
      if (metafield.key === 'center_shape') {
        acc.centerShape = metafield.value;
      }
      if (metafield.key === 'finish_level') {
        acc.finishLevel = metafield.value;
      }
      if (metafield.key === 'semi_mount_fraction') {
        acc.semiMountFraction = metafield.value;
      }
      if (metafield.key === 'complete_fraction') {
        acc.completeFraction = metafield.value;
      }
      if (metafield.key === 'side_diamond_number') {
        acc.sideDiamondNumber = parseIntOrUndefined(metafield.value);
      }
      if (metafield.key === 'metal_weight') {
        acc.metalWeight = metafield.value;
      }
      if (metafield.key === 'external_videos') {
        acc.externalVideos = JSON.parse(metafield.value) as ExternalVideo[];
      }
      return acc;
    },
    {
      ...rest,
      fractionCenter,
      centerCarat,
      externalVideos: [] as ExternalVideo[],
      metal: variant.selectedOptions.find((option) => option.name === METAL_OPTION_NAME)?.value!,
      image:
        variant.image &&
        ({
          url: variant.image.url,
          ...decodeShopifyImageAlt(variant.image.altText),
        } as RingImage),
    } as JewelryVariant
  );
};

export function getProductDetails(
  variant: JewelryVariant,
  selectedCut: string | null,
  selectedDiamondCarat: number | undefined | null
): {
  label: string;
  value: string | number | undefined | null;
  explanation?: string;
}[] {
  return [
    {
      label: 'Metal',
      value: variant.metal,
    },
    {
      label: 'Total Metal Weight',
      value: variant.metalWeight && `${variant.metalWeight} dwt`,
      explanation:
        'A dwt (pennyweight) is a unit of weight commonly used in the precious metals industry. One pennyweight is equal to 1/20 of a troy ounce, or about 1.555 grams.',
    },
    {
      label: 'Center Diamond Shape',
      value: selectedCut,
      explanation:
        'The shape of the center diamond. This price of this center diamond is not included in the price of this jewelry.',
    },
    {
      label: 'Center Diamond Carat Weight',
      value: selectedDiamondCarat,
      explanation:
        'One carat is 200 milligrams or 0.2 grams. The price of this center diamond is not included in the price of this jewelry.',
    },
    {
      label: 'Side Diamonds',
      value: variant.sideDiamondNumber,
      explanation:
        'The number of side diamonds in this piece of jewelry. The price of these side diamonds is included in the price of this jewelry.',
    },
    {
      label: 'Side Diamond Weight',
      value: variant.semiMountFraction,
      explanation:
        'The ct tw is the sum of the carat weights of all the side diamonds in this piece of jewelry. Its price is included in the price of this jewelry.',
    },
  ].filter((detail) => detail.value !== undefined && detail.value !== null);
}
